import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Stack, Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Card } from '@atlaskit/smart-card';

import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import { getContentUrl } from '../../../../utils/getContentUrl';

import { AdditionalResourceSelection } from './sub-components/AdditionalResourceSelection';
import { SourcesInput } from './sub-components/SourcesInput';

const styles = cssMap({
	sourcesContentContainer: {
		paddingInline: token('space.075'),
		paddingBlockStart: token('space.150'),
	},
	cardContainer: {
		overflow: 'hidden',
		minHeight: '27px', // same height as the Card component, to prevent layout shift
	},
});

const i18n = defineMessages({
	sourcesDescription: {
		id: 'audio.audio-popup.sources-description',
		defaultMessage: 'Customize briefings by adding sources. Hear how all the info works together.',
		description: 'The description for the sources panel in the audio popup',
	},
	thisPageHeader: {
		id: 'audio.audio-popup.sources-this-page-header',
		defaultMessage: 'This page',
		description: 'The header for the this page section in the sources panel',
	},
	additionalSourcesHeader: {
		id: 'audio.audio-popup.sources-additional-sources-header',
		defaultMessage: 'Additional sources',
		description: 'The header for the additional sources section in the sources panel',
	},
});

export const SourcesPanel = () => {
	const { formatMessage } = useIntl();

	const [
		{ activeContent, additionalResources, cloudId },
		{ addAdditionalResource, removeAdditionalResource },
	] = useAudioBriefing();

	const smartCardProviderProps = getSmartCardRenderers();

	const activeContentUrl = activeContent ? getContentUrl(activeContent) : null;

	const content = (
		<Stack space="space.050">
			<Stack xcss={styles.sourcesContentContainer} space="space.200">
				<Text size="small" color="color.text.subtlest">
					{formatMessage(i18n.sourcesDescription)}
				</Text>
				<Stack space="space.200">
					<Heading size="xxsmall">{formatMessage(i18n.thisPageHeader)}</Heading>
					<Flex xcss={styles.cardContainer} alignItems="center">
						{activeContentUrl && <Card url={activeContentUrl} appearance="inline" truncateInline />}
					</Flex>
				</Stack>
				<Stack space="space.200">
					<Heading size="xxsmall">{formatMessage(i18n.additionalSourcesHeader)}</Heading>
					<Box>
						<Stack space="space.075">
							{additionalResources.map((resource) => (
								<AdditionalResourceSelection
									key={resource.url}
									resource={resource}
									removeResource={removeAdditionalResource}
								/>
							))}
						</Stack>
						<SourcesInput
							cloudId={cloudId}
							additionalResources={additionalResources}
							addAdditionalResource={addAdditionalResource}
							activeContent={activeContent}
						/>
					</Box>
				</Stack>
			</Stack>
		</Stack>
	);

	return (
		<ConfluenceSmartCardProvider {...smartCardProviderProps}>{content}</ConfluenceSmartCardProvider>
	);
};
